import CheckCircle from '@mui/icons-material/CheckCircle'
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material'
import DriversLicensePreview, {
  DRIVER_LICENSE_FILE_ACCEPT,
  isDriverLicensePdf,
} from '@/components/DriversLicensePreview'
import { useContactDriversLicenseAdmin } from 'hooks/admin/useContactDriversLicenseAdmin'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import React, { useEffect, useState } from 'react'

interface ContactDriversLicenseSectionProps {
  contactId?: string
  storagePath?: string
  canEdit?: boolean
  /** Selected file before the contact exists (create mode). */
  pendingFile?: File | null
  onPendingFileChange?: (file: File | null) => void
}

const ContactDriversLicenseSection: React.FC<ContactDriversLicenseSectionProps> = ({
  contactId,
  storagePath,
  canEdit = true,
  pendingFile = null,
  onPendingFileChange,
}) => {
  const { showSuccess, showError } = useSnackbar()
  const isCreateMode = !contactId
  const {
    hasLicense,
    previewUrl,
    isUploading,
    isLoadingUrl,
    error,
    uploadLicense,
    removeLicense,
  } = useContactDriversLicenseAdmin(contactId, storagePath)

  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!pendingFile) {
      setPendingPreviewUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(pendingFile)
    setPendingPreviewUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [pendingFile])

  const handleUpload = async (file?: File) => {
    if (!file) return
    if (isCreateMode) {
      onPendingFileChange?.(file)
      return
    }
    try {
      await uploadLicense(file)
      showSuccess("Driver's license uploaded successfully")
    } catch {
      showError("Failed to upload driver's license. Please try again.")
    }
  }

  const handleRemove = async () => {
    if (isCreateMode) {
      onPendingFileChange?.(null)
      return
    }
    try {
      await removeLicense()
      showSuccess("Driver's license removed")
    } catch {
      showError("Failed to remove driver's license. Please try again.")
    }
  }

  const hasStoredPreview = !!previewUrl
  const hasPendingPreview = isCreateMode && !!pendingFile && !!pendingPreviewUrl
  const hasPreview = hasStoredPreview || hasPendingPreview
  const displayUrl = hasPendingPreview ? pendingPreviewUrl : previewUrl
  const showLicense = isCreateMode ? !!pendingFile : hasLicense || !!previewUrl
  const busy = isUploading || (!isCreateMode && isLoadingUrl)
  const previewIsPdf = hasPendingPreview
    ? isDriverLicensePdf(pendingFile?.name, pendingFile)
    : isDriverLicensePdf(previewUrl ?? storagePath)

  return (
    <Box>
      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1.5 }}>
        Driver&apos;s license
      </Typography>

      {busy && !isCreateMode ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : hasPreview && displayUrl ? (
        <Box position='relative' mb={2}>
          <DriversLicensePreview url={displayUrl} isPdf={previewIsPdf} maxHeight={200} />
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'success.main',
              bgcolor: 'background.paper',
              borderRadius: '50%',
              p: 0.25,
              lineHeight: 0,
            }}
          >
            <CheckCircle fontSize='small' />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: 120,
            borderRadius: 1,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            No photo or PDF uploaded yet
          </Typography>
        </Box>
      )}

      <Box display='flex' flexWrap='wrap' gap={1}>
        {canEdit && (
          <Button variant='contained' component='label' disabled={busy} size='small'>
            {isUploading ? 'Uploading...' : showLicense ? 'Replace file' : 'Upload photo or PDF'}
            <input
              hidden
              type='file'
              accept={DRIVER_LICENSE_FILE_ACCEPT}
              onChange={e => {
                const file = e.target.files?.[0]
                void handleUpload(file)
                e.currentTarget.value = ''
              }}
            />
          </Button>
        )}
        {canEdit && showLicense && (
          <Button
            variant='outlined'
            color='error'
            size='small'
            disabled={busy}
            onClick={() => void handleRemove()}
          >
            Remove
          </Button>
        )}
        {hasStoredPreview && (
          <Button
            variant='outlined'
            size='small'
            disabled={isUploading}
            onClick={() => window.open(previewUrl ?? undefined, '_blank', 'noopener,noreferrer')}
          >
            {previewIsPdf ? 'Open PDF' : 'View full size'}
          </Button>
        )}
      </Box>

      {(error || (hasLicense && !previewUrl && !isLoadingUrl && !isCreateMode)) && (
        <Typography variant='caption' color='error' sx={{ display: 'block', mt: 1 }}>
          {error ?? 'Unable to load preview.'}
        </Typography>
      )}
    </Box>
  )
}

export default ContactDriversLicenseSection
